import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  GridSmartViewsWebComponentSchema,
  SmartViewEvents,
  SmartViewInlineActions,
  SmartViewItemConfiguration,
  SmartViewSection,
} from '../types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzListItemWebComponent } from '../../../ui/gzListItem';
import { htmlEncode } from '../../../utils/escape.utils';
import { SelectOption } from '../../../ui/gzSelect/types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { elementClassListAdd } from '../../../utils/htmlElement.utils';
import { GzTextWebComponent } from '../../../ui/gzText';
import { ListItemEvents } from '../../../ui/gzListItem/types';

export class GridSmartViewsView extends BaseWebComponentView {
  private activeItem: GzListItemWebComponent;

  public static getActiveViewFromViews(views: Array<SmartViewItemConfiguration>, activeViewId: string) {
    const activeViews = views.filter(view => view.id === activeViewId);
    return activeViews.length ? activeViews[0] : views[0];
  }

  public getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$sectionsDiv = this.shadow.getElementById('sections');

    this.setTranslatedStrings();
    this.getTranslations();
  }

  public getTranslations() {
    return this.translations;
  }

  public setTranslatedStrings() {
    this.translations = this.stringsTranslator.getSmartViewsTranslations();
  }

  public buildSuccessView(data: GridSmartViewsWebComponentSchema) {
    this.dom.$sectionsDiv.innerHTML = '';
    for (const section of data.sections) {
      this.insertSectionToPanel(this.dom.$sectionsDiv, section, data.activeViewId);
    }
  }

  private insertSectionToPanel(insertionElement, smartViewSection: SmartViewSection, activeViewId: string) {
    const listWrapper = document.createElement('div');
    elementClassListAdd(listWrapper, 'smart-list');

    const sectionTitleWebComponent = new GzTitleWebComponent();
    const smartViewMaxInputLength = smartViewSection.smartViewMaxInputLength;
    const smartViewValidators = smartViewSection.smartViewValidators;
    const noSpaceEscape = smartViewSection.noSpaceEscape;

    sectionTitleWebComponent.fontSize = FontSizes.PARAGRAPH_11;
    sectionTitleWebComponent.fontWeight = FontWeights.SEMIBOLD;
    sectionTitleWebComponent.innerHTML = smartViewSection.sectionTitle.toUpperCase();
    sectionTitleWebComponent.setAttribute('data-section-type', `${smartViewSection.type}`);

    elementClassListAdd(sectionTitleWebComponent, 'section-title');
    listWrapper.appendChild(sectionTitleWebComponent);

    if (!smartViewSection.items.length) {
      this.buildEmptyView(listWrapper);
    } else {
      for (const smartViewConfiguration of smartViewSection.items) {
        listWrapper.appendChild(
          this.buildSectionItem(
            smartViewConfiguration,
            smartViewSection.inlineActions,
            activeViewId,
            smartViewValidators,
            smartViewMaxInputLength,
            noSpaceEscape
          )
        );
      }
    }

    insertionElement.appendChild(listWrapper);
  }

  protected buildEmptyView(listItem: HTMLElement) {
    const createEmptyContent = new GzListItemWebComponent();
    const createEmptyText = new GzTextWebComponent();
    createEmptyContent.emptyState = true;
    createEmptyText.fontSize = FontSizes.HEADING_6;
    createEmptyText.innerHTML = this.translations.emptySavedViewsText;
    createEmptyContent.appendChild(createEmptyText);
    listItem.appendChild(createEmptyContent);
  }

  private buildSectionItem(
    smartViewItemConfiguration: SmartViewItemConfiguration,
    inlineActions: Array<SelectOption>,
    activeViewId: string,
    smartViewValidators: Array<any> = [],
    smartViewMaxInputLength?: number,
    noSpaceEscape?: boolean
  ): HTMLElement {
    const listItem = new GzListItemWebComponent();

    this.attachListItemEventListeners(listItem, smartViewItemConfiguration);

    listItem.inlineActions =
      smartViewItemConfiguration.inlineActions !== undefined ? smartViewItemConfiguration.inlineActions : inlineActions;
    listItem.innerHTML = htmlEncode(smartViewItemConfiguration.viewName);
    listItem.itemData = smartViewItemConfiguration;

    if (noSpaceEscape) {
      listItem.noSpaceEscape = noSpaceEscape;
    }

    if (smartViewMaxInputLength) {
      listItem.inputMaxLength = smartViewMaxInputLength;
    }

    listItem.errorMessage = smartViewValidators;

    if (activeViewId === smartViewItemConfiguration.id) {
      this.updateActiveListItem(listItem);
    }

    //@ts-ignore
    if (smartViewItemConfiguration.active || this.activeItem?.itemData?.id === smartViewItemConfiguration.id) {
      this.updateItemAndTriggerSelectedEvent(listItem, smartViewItemConfiguration);
    }

    return listItem;
  }

  private attachListItemEventListeners(
    listItem: GzListItemWebComponent,
    smartViewItemConfiguration: SmartViewItemConfiguration
  ) {
    listItem.addEventListener('click', (event: MouseEvent) => {
      const isRenameButton = element =>
        element.id === 'save-input-btn' || element.classList?.contains('cancel-icon-button');
      const isInlineMenuOpen = listItem.getAttribute('class') === 'menu-open';
      const itemIsEditable = listItem.editable;
      const pathHasRenameButton = event.composedPath().find(isRenameButton);

      if (isInlineMenuOpen || itemIsEditable || pathHasRenameButton) {
        if (pathHasRenameButton) {
          this.setListItemSelectDisabled(listItem, item => (item.selectDisabled = false));
        }
      } else {
        this.updateItemAndTriggerSelectedEvent(listItem, smartViewItemConfiguration);
      }
    });

    listItem.addEventListener(SmartViewInlineActions.RENAME, (event: MouseEvent) => {
      this.setListItemSelectDisabled(listItem, item => (item.selectDisabled = !item.hasAttribute('editable')));
      listItem.selectDisabled = false;
    });

    listItem.addEventListener(ListItemEvents.CLICK_OUTSIDE, () =>
      this.setListItemSelectDisabled(listItem, item => (item.selectDisabled = false))
    );
  }

  private setListItemSelectDisabled(listItem: GzListItemWebComponent, callback: (item: GzListItemWebComponent) => any) {
    listItem.parentElement?.querySelectorAll('gz-list-item').forEach((item: GzListItemWebComponent) => callback(item));
  }

  private updateItemAndTriggerSelectedEvent(
    listItem: GzListItemWebComponent,
    smartViewItemConfiguration: SmartViewItemConfiguration
  ) {
    this.updateActiveListItem(listItem);
    listItem.dispatchEvent(
      new CustomEvent(SmartViewEvents.SMART_VIEW_SELECTED, {
        detail: {
          smartViewConfiguration: smartViewItemConfiguration,
        },
        bubbles: true,
      })
    );
  }

  private updateActiveListItem(listItem: GzListItemWebComponent) {
    // update state of current active item
    if (this.activeItem) {
      this.activeItem.active = false;
    }

    this.activeItem = listItem;
    this.activeItem.active = true;

    listItem.parentElement?.parentElement?.querySelectorAll('.smart-list').forEach((item: GzListItemWebComponent) => {
      item.querySelectorAll('gz-list-item').forEach((childListItem: GzListItemWebComponent) => {
        childListItem.selectDisabled = false;
        childListItem.editable = false;
      });
    });
  }
}
